<script lang="ts" setup>
import { computed, ref } from 'vue';
import useFlowNavigation from '@Use/useFlowNavigation';
import useCompanyImageBaseUrl from '@Src/use/useCompanyImageBaseUrl';
import { LINKS } from '@Constants/links.constants';
import { resolveWelcomeContent } from '@Constants/welcomeContent.constants';
import { RequestType } from '@Enums/requestType.enum';

// Import stores
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import { useRequestSessionStore } from '@Stores/requestSession.store';
import { useRequestDetailsStore } from '@Stores/requestDetails.store';
import { markRequestAsStartedApi } from '@Api/requestFlow.api';

// Import used components
import { ShowcaseLayout } from '@Shared';
import ShowcaseImage from '@Components/shared/ShowcaseImage.vue';

// Setup
const companyImageBaseUrl = useCompanyImageBaseUrl();
const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const { advanceFlow } = useFlowNavigation();
const requestSessionStore = useRequestSessionStore();
const requestDetailsStore = useRequestDetailsStore();

// Page state
const isNavigating = ref(false);

// Welcome content based on request type, with an EMD send request (DS-3309) getting
// its own copy. Selection lives entirely in resolveWelcomeContent so it stays in one
// place and the EMD signal (sendType) is checked the same way as the other views.
const welcomeContent = computed(() => {
    const requestType = (requestSessionStore.requestSummary?.requestType
        ?? RequestType.Unknown) as RequestType;
    return resolveWelcomeContent(requestType, requestDetailsStore.requestDetails?.sendType);
});

// Actions
const getStarted = async () => {
    isNavigating.value = true;
    try {
        await markRequestAsStartedApi();
    } catch (error) {
        console.warn('[Request MFE] markRequestAsStartedApi failed on welcome — continuing:', error);
    }
    advanceFlow();
};
</script>

<template>
    <showcase-layout
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl"
        show-feature-logo
        show-powered-by
    >
        <div class="welcome-content">
            <img
                v-if="welcomeContent"
                class="welcome-content__image"
                :src="welcomeContent.imageSrc"
                alt=""
            />

            <h1 v-if="welcomeContent">
                {{ welcomeContent.headline }}
            </h1>

            <div v-if="welcomeContent" class="welcome-content__subheadline">
                {{ welcomeContent.subheadline }}
            </div>

            <div class="welcome-content__legal">
                By continuing, you agree to our
                <app-link type="external" :link-url="LINKS.CertifID.TermsOfUse"
                >Terms of Use</app-link
                >
                and
                <app-link
                    type="external"
                    :link-url="LINKS.CertifID.PrivacyPolicy"
                >Privacy Policy</app-link
                >.
            </div>

            <app-button
                expanded
                size="large"
                @onClick="getStarted"
                :is-loading="isNavigating"
                data-id="request-welcome-get-started"
            >
                Get Started
            </app-button>
        </div>

        <template #branded>
            <showcase-image />
        </template>
        
    </showcase-layout>
</template>

<style lang="scss" scoped>
@use "certifid-v2-client-hub-styles" as *;

.welcome-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    h1 {
        margin-bottom: 0.5rem;
    }

    &__image {
        max-width: 11.125rem;
        max-height: 11.5625rem;

        @include desktop {
            display: none;
        }
    }

    &__subheadline {
        color: $theme-primary;
        font-size: 1.4375rem;
        font-style: normal;
        font-weight: 700;
        line-height: 150%;
        margin-bottom: 1.5rem;
    }

    &__legal {
        font-size: 0.75rem;
        font-style: normal;
        font-weight: 400;
        line-height: 150%;
        margin-bottom: 1.5rem;
    }
}
</style>
